import { PrismicNextImage } from '@prismicio/next';
import { asText, Content, isFilled } from '@prismicio/client';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';

import {
  Box,
  Heading,
  Typography,
  Disclosure,
  DisclosureBody,
  DisclosureSummary,
} from '@preventx/oxygen';
import { RichText } from '@preventx/prismic-services';

// Services
import { applicationEnv } from '@/services/env/shared';

// Components
import { Link } from '@/components/Actions/Link';

export type WithFrequentlyAskedQuestionsProps = {
  id: string;
  slice: Content.RegionComplexSliceWithFaqs;
};

export const WithFrequentlyAskedQuestions = ({
  id,
  slice,
}: WithFrequentlyAskedQuestionsProps) => {
  const { primary } = slice;

  const { faqs, image, title, eyebrow, support_page_link } = primary;

  const faqBaseKey = `faq-item-${id}`;

  const { WebsiteUrl } = applicationEnv();

  return (
    <>
      <Box component="div" className="headline items-end" addRepel>
        <Box component="div" addFlow>
          <Typography component="p" addEyebrow>
            {asText(eyebrow)}
          </Typography>
          <Heading component="h2" addHeadlineTitle>
            {asText(title)}
          </Heading>
        </Box>

        {isFilled.link(support_page_link) && (
          <Link
            as="default"
            type="button"
            variant="outline"
            retainSearchParams
            href={support_page_link}
            icon={{
              position: 'right',
              definition: faArrowRight,
              addDirectionalIcon: true,
            }}
          >
            {support_page_link.text}
          </Link>
        )}
      </Box>
      <Box component="div" gap="gap-lg" addSwitcher>
        <figure className="feature-img">
          {isFilled.image(image) && <PrismicNextImage field={image} />}
        </figure>

        <Box component="div" addFlow>
          {faqs.map(({ title, content, link }, index) => {
            const iteration = index + 1;

            return (
              <Disclosure
                name={faqBaseKey}
                variant="primary"
                open={iteration === 1}
                key={`${faqBaseKey}-${index + 1}`}
              >
                <DisclosureSummary>{asText(title)}</DisclosureSummary>

                <DisclosureBody>
                  <RichText
                    content={content}
                    variant="default"
                    websiteUrl={WebsiteUrl}
                  />

                  {isFilled.link(link) && (
                    <Link
                      href={link}
                      as="default"
                      type="button"
                      colour="primary"
                      variant="outline"
                      retainSearchParams
                      icon={{
                        position: 'right',
                        definition: faArrowRight,
                        addDirectionalIcon: true,
                      }}
                    >
                      {link.text}
                    </Link>
                  )}
                </DisclosureBody>
              </Disclosure>
            );
          })}
        </Box>
      </Box>
    </>
  );
};
